<!--
  The closing band, and the footer inside it.

  These used to be two sections: a band with the closing ask, then a footer on
  the ground below it. That meant two sets of store badges a screen apart and
  two blocks of chrome doing one job. They are one section now, so the page
  ends the way it began, on the band.

  The links sit beside the ask rather than under it, on the right. A footer
  that stacks a call to action and then a separate rule and then a row of links
  reads as three blocks; side by side it reads as one.

  There is exactly one brand mark here, and it is the logo lockup rather than a
  tracked-out LEDGERLY kicker: a wordmark and a kicker saying the same word two
  lines apart is the same thing twice.

  `withCta` is true only on the landing page. On /terms and /privacy the same
  component renders the mark, the links and the copyright.
-->
<footer class="band">
  <div class="mx-auto max-w-[1180px] px-8">
    <div
      class="grid gap-16 lg:grid-cols-[1fr_auto] lg:gap-24"
      [class]="withCta() ? 'pt-[112px] pb-[56px]' : 'py-[56px]'"
    >
      <div>
        <a
          [href]="homeHref"
          class="inline-flex text-neutral-100"
          aria-label="Ledgerly"
        >
          <layout-logo [onBand]="true" />
        </a>

        @if (withCta()) {
        <h2
          class="mt-8 max-w-[18ch] text-headline text-neutral-100 text-balance"
          i18n="@@downloadHeadline"
        >
          Keep every day written down.
        </h2>

        <p
          class="mt-8 max-w-[46ch] text-body text-accent-200"
          i18n="@@downloadBodyTrial"
        >
          Free to start, with everything unlocked. If you do not buy, or a
          subscription ends, reading, searching, exporting and backup keep
          working. Only recording new transactions stops.
        </p>

        <div class="mt-[42px]">
          <layout-store-badges location="footer-cta" [onBand]="true" />
        </div>

        <p
          class="mt-6 max-w-[44ch] text-sub text-accent-300"
          i18n="@@downloadPriceNote"
        >
          After that it is a paid app, at the price your App Store or Google
          Play charges in your country.
        </p>
        }
      </div>

      <nav class="flex flex-wrap gap-16 sm:gap-24 lg:justify-end">
        <div class="flex flex-col gap-4">
          <p
            class="text-kicker uppercase text-accent-300"
            i18n="@@footerPageKicker"
          >
            This page
          </p>
          @for (link of navLinks; track link.path) {
          <a
            [routerLink]="['/']"
            [fragment]="link.path.substring(1)"
            class="text-label text-accent-200"
            >{{ link.label }}</a
          >
          }
        </div>

        <div class="flex flex-col gap-4">
          <p
            class="text-kicker uppercase text-accent-300"
            i18n="@@footerMoreKicker"
          >
            More
          </p>
          <!-- A route, not a fragment, so it is not in NAV_LINKS. -->
          <a routerLink="/blog" class="text-label text-accent-200">Blog</a>
        </div>

        <div class="flex flex-col gap-4">
          <p
            class="text-kicker uppercase text-accent-300"
            i18n="@@footerLegalKicker"
          >
            The small print
          </p>
          <a
            routerLink="/facts"
            class="text-label text-accent-200"
            i18n="@@footerFacts"
            >Ledgerly facts</a
          >
          <a
            routerLink="/terms"
            class="text-label text-accent-200"
            i18n="@@footerTerms"
            >Terms</a
          >
          <a
            routerLink="/privacy"
            class="text-label text-accent-200"
            i18n="@@footerPrivacy"
            >Privacy</a
          >
          <a
            href="https://x.com/UseLedgerly"
            target="_blank"
            rel="noopener"
            class="text-label text-accent-200"
            i18n="@@footerSocial"
            >Ledgerly on X</a
          >
          <a
            href="mailto:hello@ledgerly.download"
            class="text-label text-accent-200"
            >hello&#64;ledgerly.download</a
          >
        </div>
      </nav>
    </div>

    <!--
      No link on the company name. A copyright line says who owns the work; it
      is not a place to send people somewhere else.
    -->
    <div class="border-t border-neutral-100/15 py-8">
      <p class="text-sub text-accent-300">
        Copyright &copy; {{ year }} Jineo Technologies. {{ ' '
        }}<span i18n="@@footerRights">All rights reserved.</span>
      </p>
    </div>
  </div>
</footer>
